import { BottomSheetModal } from '@gorhom/bottom-sheet';
import React, {
  createContext,
  useCallback,
  useState,
  ReactNode,
  useRef,
} from 'react';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import {
  selectSaleCustomer,
  setSaleCustomerId,
  setSaleCustomerName,
} from '../redux/cartSlice';

interface ModalContextType {
  visibleName: boolean;
  /** Name of the customer on the sale in the cart (`cart.customer`); '' when none. */
  input: string;
  setInput: (value: string) => void;
  assignedStaffId: string | null;
  setAssignedStaffId: React.Dispatch<React.SetStateAction<string | null>>;
  /** Back-office id of the customer on the sale in the cart (`cart.customer`). */
  selectedCustomerId: string | null;
  setSelectedCustomerId: (value: string | null) => void;
  setVisibleName: React.Dispatch<React.SetStateAction<boolean>>;
  modalVisible: boolean;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  bottomSheetHomeModalRef: React.RefObject<BottomSheetModal | null>;
  paymentModal: boolean;
  setPaymentModal: React.Dispatch<React.SetStateAction<boolean>>;
  mainModal: boolean;
  setMainModal: React.Dispatch<React.SetStateAction<boolean>>;
  filterModal: boolean;
  setFilterModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ModalContext = createContext<ModalContextType>(
  {} as ModalContextType,
);

interface ModalContextProviderObject {
  children: ReactNode;
}

export const ModalContextProvider: React.FC<ModalContextProviderObject> = ({
  children,
}) => {
  const bottomSheetHomeModalRef = useRef<BottomSheetModal | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [visibleName, setVisibleName] = useState<boolean>(false);
  const [paymentModal, setPaymentModal] = useState<boolean>(false);
  const [mainModal, setMainModal] = useState<boolean>(false);
  const [filterModal, setFilterModal] = useState<boolean>(false);

  const [assignedStaffId, setAssignedStaffId] = useState<string | null>(null);

  // The sale's customer lives in the cart slice so that clearing the cart
  // clears it everywhere; `input` / `selectedCustomerId` keep the context API
  // the Save Order sheet and the cart were written against.
  const dispatch = useAppDispatch();
  const saleCustomer = useAppSelector(selectSaleCustomer);
  const input = saleCustomer?.name ?? '';
  const selectedCustomerId = saleCustomer?.id ?? null;
  const setInput = useCallback(
    (value: string) => {
      dispatch(setSaleCustomerName(value));
    },
    [dispatch],
  );
  const setSelectedCustomerId = useCallback(
    (value: string | null) => {
      dispatch(setSaleCustomerId(value));
    },
    [dispatch],
  );

  return (
    <ModalContext.Provider
      value={{
        visibleName,
        input,
        setInput,
        assignedStaffId,
        setAssignedStaffId,
        selectedCustomerId,
        setSelectedCustomerId,
        setVisibleName,
        modalVisible,
        setModalVisible,
        bottomSheetHomeModalRef,
        paymentModal,
        setPaymentModal,
        mainModal,
        setMainModal,
        filterModal,
        setFilterModal,
      }}>
      {children}
    </ModalContext.Provider>
  );
};
